import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { copilot } from "@harmony/enablers/react";
import { Button, Copilot, Dialog, Icon } from "../../common/HarmonyEnablers";
import { AppDispatch, RootState } from "../../root-redux/RootState";
import { postNebulaSupportAgentMessage, troubleshootWorkflow } from "../../services/NebulaSupportAgent/NebulaSupportAgent.api";
import { resetNebulaSupportAgent } from "./NebulaSupportAgent.redux";
import { RequestStatus } from "../../model/ServiceRequestStatus.model";
import { permissionsSelector } from "../../shared-components/role-based-access-control/RoleBasedAccessControl.selectors.redux";
import { Logger } from "../../utils/Logger";
import { ErrorNotification } from "../notifications/ErrorNotification";

const startingMessage =
    "Hello! I am **Nebula Support Agent**. How can I help you today?\n\n" +
    "Try asking:\n\n" +
    "   🔄 Refresh cache for my tenant\n\n" +
    "   🔀 Swap cluster for my environment\n\n" +
    "   🛠️ Help me troubleshoot an API error\n\n" +
    "   📖 Guide me through onboarding\n\n";

const workflowStartingMessage = (workflowName: string) =>
    `I see you have workflow **${workflowName}** selected.\n\n` +
    "Try asking:\n\n" +
    "   🔧 This workflow is failing. Fix it.\n\n" +
    "   📊 Check the status of this workflow\n\n" +
    "   🔀 Swap the cluster if there's an issue\n\n";

interface NebulaSupportAgentProps {
    resetKey?: number;
    onNewSession?: () => void;
}

export const NebulaSupportAgent: React.FC<NebulaSupportAgentProps> = ({ resetKey = 0, onNewSession }) => {
    const copilotRef = useRef<copilot>(null);
    const dispatch = useDispatch<AppDispatch>();
    const [firstMsg, setFirstMsg] = useState(true);
    const [responses, setResponses] = useState<string[]>(["Init"]);
    const [feedbackOpen, setFeedbackOpen] = useState<{ index: number; type: 'like' | 'dislike' } | null>(null);
    const [feedbackText, setFeedbackText] = useState("");
    const [showErrorNotif, setShowErrorNotif] = useState(false);
    const [errorText, setErrorText] = useState("");
    const [sessionKey, setSessionKey] = useState(0);

    const agentState = useSelector((state: RootState) => state.nebulaSupportAgent);
    const selectedWorkflow = useSelector((state: RootState) => state.workflowById.workflow);
    const permissionsList = useSelector(permissionsSelector);
    const hasWorkflowContext = selectedWorkflow?.config?.workflowId && selectedWorkflow?.subscriptionKey;
    const prevHasWorkflowContext = useRef(hasWorkflowContext);
    const prevResetKey = useRef(resetKey);

    // Handle external reset trigger (New Session button in header)
    useEffect(() => {
        if (prevResetKey.current !== resetKey && resetKey > 0) {
            prevResetKey.current = resetKey;
            setFirstMsg(true);
            setResponses(["Init"]);
            setSessionKey(k => k + 1);
        }
    }, [resetKey]);

    // Notify in chat when workflow context changes (select/deselect workflow)
    useEffect(() => {
        if (prevHasWorkflowContext.current !== hasWorkflowContext) {
            prevHasWorkflowContext.current = hasWorkflowContext;
            const msg = hasWorkflowContext
                ? workflowStartingMessage(selectedWorkflow.config.workflowName || selectedWorkflow.config.workflowId)
                : startingMessage;
            copilotRef.current?.addResponse({ value: msg, hideFeedback: true });
            setResponses(prev => [...prev, "context-change"]);
        }
    }, [hasWorkflowContext, selectedWorkflow]);

    useEffect(() => {
        const host: any = copilotRef.current;
        if (!host) return;
        const shadow = host.shadowRoot;
        if (shadow) {
            const content = shadow.querySelector(".chat__window > .chat__content");
            if (content) {
                const offset = hasWorkflowContext ? "210px" : "175px";
                content.setAttribute("style", `overflow-y: auto; height: calc(100vh - ${offset});`);
            }
            if (!shadow.querySelector("#nebula-agent-overrides")) {
                const style = document.createElement("style");
                style.id = "nebula-agent-overrides";
                style.textContent = `.chat__message { word-break: break-word; overflow-wrap: break-word; }`;
                shadow.appendChild(style);
            }
        }
        if (firstMsg) {
            const msg = hasWorkflowContext
                ? workflowStartingMessage(selectedWorkflow.config.workflowName || selectedWorkflow.config.workflowId)
                : startingMessage;
            copilotRef.current?.addResponse({ value: msg, hideFeedback: true });
            setFirstMsg(false);
        }
    }, [agentState.status, hasWorkflowContext, sessionKey]);

    const handleSuccessResponse = (result: any) => {
        let responseMessage = "Something went wrong, please rephrase your prompt.";
        if (result.meta.requestStatus === "fulfilled") {
            responseMessage = result.payload.response || responseMessage;
        } else if (result.payload?.correlationId) {
            const { correlationId, statusCode } = result.payload;
            let errorMsg = "An unexpected error occurred.";
            if (statusCode === 0 || statusCode === 408) errorMsg = "Request timed out or network error. Please try again.";
            else if (statusCode === 401 || statusCode === 403) errorMsg = "Access denied. You may not have access to the workflow.";
            else if (statusCode === 404) errorMsg = "The requested resource was not found.";
            else if (statusCode >= 500) errorMsg = "A server error occurred. Please try again later.";
            setErrorText(`${errorMsg} (Correlation ID: ${correlationId})`);
            setShowErrorNotif(true);
        }
        setResponses(prev => [...prev, responseMessage]);
        copilotRef.current?.addResponse({ value: responseMessage, hideFeedback: true });

        // Auto-scroll to the bottom after response is added
        setTimeout(() => {
            const host: any = copilotRef.current;
            const shadow = host?.shadowRoot;
            const content = shadow?.querySelector(".chat__window > .chat__content");
            if (content) {
                content.scrollTop = content.scrollHeight;
            }
        }, 150);
    };

    const handleFailedResponse = () => {
        copilotRef.current?.addResponse({
            value: "An error occurred while processing your request.",
            hideFeedback: true,
        });
    };

    const handleInput = (e: any) => {
        const message = e.detail?.value;
        if (!message || !message.trim()) return; // ignore empty/phantom events from remount

        const doRequest = () => {
            if (hasWorkflowContext) {
                return dispatch(troubleshootWorkflow({
                    workflowId: selectedWorkflow.config.workflowId,
                    subscriptionKeys: permissionsList.userTeams.key,
                    message,
                }));
            }
            return dispatch(postNebulaSupportAgentMessage({ message }));
        };

        // Retry once for new sessions (threadId is null) since thread creation can transiently fail
        const isNewSession = !agentState.threadId;

        doRequest()
            .then((result: any) => {
                if (isNewSession && result.meta.requestStatus !== "fulfilled") {
                    const statusCode = result.payload?.statusCode;
                    if (statusCode >= 500 || statusCode === 0 || statusCode === 408) {
                        setTimeout(() => {
                            doRequest()
                                .then(handleSuccessResponse)
                                .catch(handleFailedResponse);
                        }, 1500);
                        return;
                    }
                }
                handleSuccessResponse(result);
            })
            .catch(handleFailedResponse);
    };

    const handleFeedback = (i: number, option: number) => {
        let disable = "", disable2 = "", enable = "", enable2 = "";
        switch (option) {
            case 1:
                [disable, disable2] = [`like-${i}-1`, `dislike-${i}-4`];
                [enable, enable2] = [`like-${i}-3`, `dislike-${i}-2`];
                break;
            case 2:
                [disable, disable2] = [`dislike-${i}-2`, `like-${i}-3`];
                [enable, enable2] = [`like-${i}-1`, `dislike-${i}-4`];
                break;
            case 3:
                [disable, disable2] = [`dislike-${i}-4`, `like-${i}-3`];
                [enable, enable2] = [`like-${i}-1`, `dislike-${i}-2`];
                break;
            case 4:
                [disable, disable2] = [`dislike-${i}-4`, `like-${i}-3`];
                [enable, enable2] = [`like-${i}-1`, `dislike-${i}-2`];
                break;
        }
        document.getElementById(disable)?.setAttribute("style", "display: none;");
        document.getElementById(disable2)?.setAttribute("style", "display: none;");
        document.getElementById(enable)?.setAttribute("style", "display: block;");
        document.getElementById(enable2)?.setAttribute("style", "display: block;");
        const feedbackType = (option === 1 || option === 3) ? 'like' : 'dislike';

        Logger.Instance.trackTrace('NebulaSupportAgent-Feedback', {
            responseIndex: i.toString(),
            feedbackType,
            threadId: agentState.threadId || '',
        });

        if (option === 1) {
            setFeedbackOpen({ index: i, type: 'like' });
        } else if (option === 2) {
            setFeedbackText("");
            setFeedbackOpen({ index: i, type: 'dislike' });
        }
    };

    const submitFeedback = () => {
        if (feedbackOpen && feedbackOpen.type === 'dislike' && feedbackText.trim()) {
            Logger.Instance.trackTrace('NebulaSupportAgent-FeedbackText', {
                responseIndex: feedbackOpen.index.toString(),
                feedbackType: 'dislike',
                feedbackText: feedbackText.trim(),
                threadId: agentState.threadId || '',
            });
        }
        setFeedbackText("");
        setFeedbackOpen(null);
    };

    return (
        <>
        {hasWorkflowContext && (
            <div style={{
                display: "flex", alignItems: "center",
                padding: "8px 12px", backgroundColor: "#e6f2ff", borderBottom: "1px solid #c8dff5",
                fontSize: "13px",
            }}>
                <Icon name="processing" style={{ marginRight: "6px", fontSize: "14px" }} />
                <span>Workflow: <strong>{selectedWorkflow.config.workflowName || selectedWorkflow.config.workflowId}</strong></span>
            </div>
        )}
        <Copilot
            key={sessionKey}
            disableInput={agentState.status === RequestStatus.loading}
            loading={agentState.status === RequestStatus.loading}
            hideStopGenerationButton
            maxlength={2500}
            onHeCopilotInput={handleInput}
            ref={copilotRef}
            label="Nebula Support Agent"
            style={{ height: "100%" }}
        >
            {responses.map((_, i) => (
                <React.Fragment key={i}>
                    <div slot={`response-${i + 1}-ai-message`}>
                        <span style={{ fontSize: "12.5px" }}>AI-generated content may be incorrect</span>
                    </div>
                    <div slot={`response-${i + 1}-feedback`}>
                        <span style={{ display: "flex", gap: "8px", marginLeft: "auto", color: "#006cbe", cursor: "pointer" }}>
                            <Icon id={`like-${i + 1}-1`} name="like" onClick={() => handleFeedback(i + 1, 1)} />
                            <Icon id={`like-${i + 1}-3`} style={{ display: "none" }} name="likesolid" onClick={() => handleFeedback(i + 1, 3)} />
                            <Icon id={`dislike-${i + 1}-2`} name="dislike" onClick={() => handleFeedback(i + 1, 2)} />
                            <Icon id={`dislike-${i + 1}-4`} style={{ display: "none" }} name="dislikesolid" onClick={() => handleFeedback(i + 1, 4)} />
                        </span>
                    </div>
                </React.Fragment>
            ))}
        </Copilot>

        <Dialog heading="Nebula Support Agent Feedback" open={feedbackOpen !== null} onHeHide={(e: any) => { e.stopPropagation(); setFeedbackOpen(null); }}>
            {feedbackOpen?.type === 'dislike' ? (
                <>
                    <p>Sorry to hear that. What went wrong?</p>
                    <textarea
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        placeholder="Tell us what could be improved..."
                        rows={4}
                        style={{ width: "100%", resize: "vertical", padding: "8px", fontSize: "14px" }}
                        maxLength={1000}
                    />
                </>
            ) : (
                <p>Thanks for the positive feedback!</p>
            )}
            <Button slot="footer" appearance="primary" onClick={submitFeedback}>
                {feedbackOpen?.type === 'dislike' ? 'Submit' : 'OK'}
            </Button>
        </Dialog>
        <ErrorNotification errorNotif={showErrorNotif} seterrornotif={setShowErrorNotif} errorText={errorText} />
        </>
    );
};
